import { handleApiError } from 'api/error-handling';
import { isAxiosError } from 'axios';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { IssueBulkWaiver, IssueBulkWaiverFailure, IssueBulkWaiverResult } from 'types/generated';
import { client } from 'utils/api-client';

// Each request stays far inside the API timeout; chunks run one after another so the
// progress count is accurate and a large batch is spread out on the email queue.
const CHUNK_SIZE = 25;

export interface SendWaiversVariables {
  athleteIds: string[];
  templateId: string;
  onProgress: (processed: number) => void;
}

/** A request the server rejected outright (bad template, no permission), so later chunks would fail too. */
export class SendWaiversStoppedError extends Error {
  constructor(message: string, public partialResult: IssueBulkWaiverResult) {
    super(message);
  }
}

export default function useSendWaivers(): UseMutationResult<
  IssueBulkWaiverResult,
  SendWaiversStoppedError,
  SendWaiversVariables
> {
  const queryClient = useQueryClient();

  const sendWaivers = async ({
    athleteIds,
    templateId,
    onProgress,
  }: SendWaiversVariables): Promise<IssueBulkWaiverResult> => {
    const result: IssueBulkWaiverResult = { issued: 0, sentExisting: 0, failed: [] };
    for (let start = 0; start < athleteIds.length; start += CHUNK_SIZE) {
      const chunk = athleteIds.slice(start, start + CHUNK_SIZE);
      try {
        const payload: IssueBulkWaiver = { athleteIds: chunk, template: templateId };
        const { data } = await client.post<IssueBulkWaiverResult>('documents/issue-bulk/', payload);
        result.issued += data.issued;
        result.sentExisting += data.sentExisting;
        result.failed.push(...data.failed);
      } catch (error) {
        const status = isAxiosError(error) ? error.response?.status : undefined;
        if (status !== undefined && status >= 400 && status < 500) {
          throw new SendWaiversStoppedError(handleApiError(error).message, result);
        }
        const failures: IssueBulkWaiverFailure[] = chunk.map(athleteId => ({
          athleteId,
          name: null,
          reason: 'REQUEST_FAILED',
        }));
        result.failed.push(...failures);
      }
      onProgress(Math.min(start + CHUNK_SIZE, athleteIds.length));
    }
    return result;
  };

  return useMutation(sendWaivers, {
    onSettled: () => {
      queryClient.invalidateQueries('documents');
      queryClient.invalidateQueries('waiver-candidates');
    },
  });
}
